import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateAttribute } from "../../../../actions/flowActions";
import { getSAATapadDeliveryTemplates } from "../../../../actions/saaAdvertisersActions";
import { labelCompare } from "../../../../helpers/selectHelper";
import { FormControl, InputLabel, MenuItem, Select, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { IAppState } from "../../../../types/stores";
import { deleteFlowExportSAATemplateFields, invalidateItemAndChildren } from "../../../../actions/flowActions";
import { getSAASegmentFieldList } from "../../../../actions/saaSegmentFieldsActions";
import { GetSAADeploySettings } from "../../../../actions/companiesActions";

type Props = {
    flowExport: any;
    canEdit: boolean;
    saaTemplateDeploySetting: string | undefined;
};

const SAATemplateTypes: React.FC<Props> = ({ flowExport, canEdit, saaTemplateDeploySetting }: Props) => {
    const dispatch = useDispatch();

    const flow = useSelector((state: IAppState) => state.flows.byId[state.selected.flow]);
    const saaTapadDeliveryTypes = useSelector((state: IAppState) => state.vars.saaTapadDeliveryTypes);
    const companyId = useSelector((state: IAppState) => state.session.companyId);
    const isCompanyVerticalsLoading = useSelector((state: IAppState) => state.companyEdit.isLoadingVerticals);
    const flowExportSAATemplateFields = useSelector((state: IAppState) => state.flowExportSAATemplateFields);
    const saaDeploySettings = useSelector((state: IAppState) => state.companyEdit.saaTemplateSettings);
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures);
    const [selectedDeploySetting, setSelectedDeploySetting] = useState<number | undefined>();
    const [filteredSAATemplates, setFilteredSAATemplates] = useState<any[]>([]);
    const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>(() =>
        flowExport.DeliveryTypeId ? flowExport.DeliveryTypeId.split(",").map(Number) : []
    );

    const getTemplates = (activationChannel?: number) =>
        saaTapadDeliveryTypes
            .filter(x => x.SAATemplate.TemplateId === activationChannel && x.SAATemplate.CompanyID === companyId)
            .map(item => ({
                label: item.SAATemplate.Name,
                value: item.SAATemplate.FileBasedSAATemplateId,
                item,
            }))
            .sort(labelCompare);

    const destinationDeliveryTypes = useMemo(
        () => saaTapadDeliveryTypes.filter(x => x.SAATemplate.TemplateId === selectedDeploySetting),
        [saaTapadDeliveryTypes, selectedDeploySetting]
    );

    useEffect(() => {
        if (
            companyId != null &&
            !isCompanyVerticalsLoading &&
            (!destinationDeliveryTypes || destinationDeliveryTypes.length == 0)
        ) {
            dispatch(getSAATapadDeliveryTemplates(companyId));
            dispatch(getSAASegmentFieldList());
            dispatch(GetSAADeploySettings());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const activationChannel = flowExport.ActivationChannelId;
        setSelectedDeploySetting(activationChannel);
        setFilteredSAATemplates(getTemplates(activationChannel));
    }, [flowExport, saaTapadDeliveryTypes]);

    const handleActivationChannelChange = (_, selectedOption) => {
        const selectedActivationChannel = selectedOption?.value || undefined;
        setSelectedDeploySetting(selectedActivationChannel);

        setFilteredSAATemplates([]);
        dispatch(updateAttribute("flowExports", flowExport.FlowExportId, "DeliveryTypeId", null));

        let saaObjects = Object.keys(flowExportSAATemplateFields.byId).map(x => flowExportSAATemplateFields.byId[x]);

        const removeTemplates = saaObjects.filter(
            z => z.FlowItemId == flowExport.FlowItemId && z.TemplateId == flowExport.DeliveryTypeId
        );

        if (removeTemplates && removeTemplates.length > 0) {
            removeTemplates.forEach(x => {
                dispatch(deleteFlowExportSAATemplateFields(x.FlowExportSAATemplateFieldId));
            });
            dispatch(invalidateItemAndChildren(flowExport.FlowItemId));
        }

        dispatch(
            updateAttribute("flowExports", flowExport.FlowExportId, "ActivationChannelId", selectedActivationChannel)
        );

        setFilteredSAATemplates(getTemplates(selectedActivationChannel));
    };

    // Get activation channels based on deploy settings
    const saaActivationChannels = useMemo(() => {
        const deploySettingIds = saaTemplateDeploySetting?.split(";").map(Number) || [];

        return saaDeploySettings
            .filter(setting => deploySettingIds.includes(setting.TemplateId))
            .map(setting => ({
                label: `${setting.Name} - ${setting.DeliveryType}`,
                value: setting.TemplateId,
                item: setting,
            }));
    }, [saaTemplateDeploySetting, saaDeploySettings]);

    const tapadDeliveryTypeChangedArr = event => {
        const {
            target: { value },
        } = event;

        if (enabledFeatures.includes("saa-multi-select")) {
            const valueArr: number[] = Array.isArray(value) ? value : value != null ? [value] : [];
            dispatch(
                updateAttribute(
                    "flowExports",
                    flowExport.FlowExportId,
                    "DeliveryTypeId",
                    valueArr.length > 0 ? valueArr.join(",") : null
                )
            );
            dispatch(invalidateItemAndChildren(flowExport.FlowItemId));

            setSelectedTemplateIds(valueArr);
        } else {
            // On autofill we get a stringified value.
            const valueArray =
                typeof value === "string"
                    ? value.split(",").map(Number)
                    : Array.isArray(value)
                      ? value
                      : typeof value === "number"
                        ? [value]
                        : [];

            if (valueArray && valueArray.includes(0)) {
                console.log(valueArray);
                dispatch(updateAttribute("flowExports", flowExport.FlowExportId, "DeliveryTypeId", null));
                // clear previously executed results
                setSelectedTemplateIds([]);
                dispatch(invalidateItemAndChildren(flowExport.FlowItemId));
            } else {
                console.log(valueArray);
                setSelectedTemplateIds(valueArray);
                dispatch(
                    updateAttribute("flowExports", flowExport.FlowExportId, "DeliveryTypeId", valueArray.join(","))
                );
                // clear previously executed results
                dispatch(invalidateItemAndChildren(flowExport.FlowItemId));
            }
        }
    };

    return (
        <>
            {/* Activation Channel Selection */}
            <FormControl fullWidth required>
                <Autocomplete
                    id="activation-channel"
                    options={saaActivationChannels}
                    getOptionLabel={option => option?.label || ""}
                    getOptionSelected={(option, value) => value.value === option.value}
                    onChange={handleActivationChannelChange}
                    autoComplete
                    value={saaActivationChannels.find(channel => channel.value === selectedDeploySetting) || null}
                    disabled={!canEdit || (flow && flow.IsLocked)}
                    renderInput={params => <TextField {...params} label="Activation Channel" fullWidth />}
                />
            </FormControl>

            {/* Activation Template Selection */}

            <FormControl style={{ width: "100%", margin: "10px 0px" }}>
                <InputLabel id="SAADeploySettingSelect-label" shrink>
                    SAA File Based Setting
                </InputLabel>
                <Select
                    labelId="SAADeploySettingId-label"
                    id="SAADeploySettingId"
                    name="SAADeploySettingSelectId-select"
                    multiple={enabledFeatures.includes("saa-multi-select")}
                    value={
                        enabledFeatures.includes("saa-multi-select")
                            ? selectedTemplateIds
                            : selectedTemplateIds.length > 0
                              ? selectedTemplateIds[0]
                              : ""
                    }
                    onChange={tapadDeliveryTypeChangedArr}
                >
                    {filteredSAATemplates &&
                        filteredSAATemplates.map(x => (
                            <MenuItem key={"setting-" + x.value} value={x.value}>
                                {x.label}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>
        </>
    );
};

export default SAATemplateTypes;
